import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Lock, Sparkles, Star, Rocket, Check, ArrowRight, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Premium Upgrade Modal
 * Displays when users try to access premium features
 * STANDARD USERS ALWAYS SEE STANDARD PRICING ($14.99/month, $120/year)
 */
export default function UpgradeModal({ 
  isOpen, 
  onClose, 
  toolName = "this feature",
  toolDescription = ""
}) {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  // Standard pricing for regular users
  const STANDARD_MONTHLY = 14.99;
  const STANDARD_YEARLY = 120;
  const TRIAL_PRICE = 4;
  const TRIAL_DAYS = 3;

  useEffect(() => {
    if (isOpen) {
      fetchPricing();
    }
  }, [isOpen]);

  const fetchPricing = async () => {
    try {
      // Fetch standard pricing config
      const pricingRes = await fetch(`${API_URL}/api/subscriptions/pricing-config`);
      if (pricingRes.ok) {
        const data = await pricingRes.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    onClose();
    // Always navigate to standard pricing page for standard users
    navigate('/pricing');
  };

  // Standard pricing display - NO special pricing for Founders/Early Adopters here
  const monthlyPrice = pricing?.monthly_price || STANDARD_MONTHLY;
  const yearlyPrice = pricing?.yearly_price || STANDARD_YEARLY;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">Unlock Premium Tools</DialogTitle>
              {toolName !== "this feature" && (
                <p className="text-sm text-gray-500">{toolName}</p>
              )}
            </div>
          </div>
          <DialogDescription className="text-left">
            {toolDescription || "Upgrade to Premium to unlock all tools and features."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Pricing Options */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleUpgrade}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left group"
              >
                <p className="text-sm text-gray-500">Monthly</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${monthlyPrice}
                  <span className="text-sm font-normal text-gray-500">/mo</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">Billed monthly</p>
              </button>
              <button
                onClick={handleUpgrade}
                className="p-4 border-2 border-purple-500 bg-purple-50 rounded-xl hover:bg-purple-100 transition-all text-left relative"
              >
                <Badge className="absolute -top-2 -right-2 bg-purple-600">Best Value</Badge>
                <p className="text-sm text-gray-500">Yearly</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${yearlyPrice}
                  <span className="text-sm font-normal text-gray-500">/yr</span>
                </p>
                <p className="text-xs text-green-600 mt-1">Save ${((monthlyPrice * 12) - yearlyPrice).toFixed(0)}/year</p>
              </button>
            </div>

            {/* Features Preview */}
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">What you&apos;ll unlock:</p>
              <ul className="space-y-2">
                {[
                  "All premium career tools",
                  "AI-powered resume builder",
                  "LinkedIn optimizer",
                  "Unlimited cover letters",
                  "Advanced job tracking"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {/* Trial Option */}
              <Button 
                onClick={() => {
                  onClose();
                  window.location.href = '/checkout?plan=monthly';
                }}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                data-testid="start-trial-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Try {TRIAL_DAYS} Days for ${TRIAL_PRICE}
              </Button>
              <Button 
                onClick={handleUpgrade}
                variant="outline"
                className="w-full border-purple-200 hover:bg-purple-50"
                data-testid="upgrade-now-btn"
              >
                View All Plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="ghost" 
                onClick={onClose}
                className="w-full text-gray-500"
              >
                Not now
              </Button>
            </div>
            <p className="text-xs text-center text-gray-400">
              Trial: ${TRIAL_PRICE} for {TRIAL_DAYS} days • Then ${monthlyPrice}/month
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Premium Tool Card Overlay
 * Displays over locked premium tools on the dashboard
 */
export function PremiumOverlay({ onClick }) {
  return (
    <div 
      className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-900/90 backdrop-blur-[2px] rounded-xl flex flex-col items-center justify-center cursor-pointer group z-10"
      onClick={onClick}
      data-testid="premium-overlay"
    >
      {/* Diagonal Watermark */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-30deg] text-white/10 text-lg font-bold whitespace-nowrap">
          Premium Feature
        </div>
      </div>
      
      <Lock className="w-8 h-8 text-white/70 mb-2 group-hover:scale-110 transition-transform" />
      <span className="text-white/90 text-sm font-medium">Upgrade to unlock</span>
    </div>
  );
}

/**
 * Premium Badge Component
 */
export function PremiumBadge({ size = 'default' }) {
  const sizeClasses = {
    small: 'text-[10px] px-1.5 py-0.5',
    default: 'text-xs px-2 py-0.5',
    large: 'text-sm px-3 py-1'
  };
  
  return (
    <Badge 
      className={`bg-gradient-to-r from-purple-500 to-indigo-500 text-white ${sizeClasses[size]}`}
      data-testid="premium-badge"
    >
      <Sparkles className="w-3 h-3 mr-1" />
      Premium
    </Badge>
  );
}

/**
 * Founder Badge Component
 */
export function FounderBadge({ size = 'default' }) {
  const sizeClasses = {
    small: 'text-[10px] px-1.5 py-0.5',
    default: 'text-xs px-2 py-0.5',
    large: 'text-sm px-3 py-1'
  };
  
  return (
    <Badge 
      className={`bg-gradient-to-r from-amber-500 to-yellow-500 text-white ${sizeClasses[size]}`}
      data-testid="founder-badge"
    >
      <Star className="w-3 h-3 mr-1" />
      Founder Circle
    </Badge>
  );
}

/**
 * Early Adopter Badge Component
 */
export function EarlyAdopterBadge({ number, size = 'default' }) {
  const sizeClasses = {
    small: 'text-[10px] px-1.5 py-0.5',
    default: 'text-xs px-2 py-0.5',
    large: 'text-sm px-3 py-1'
  };
  
  return (
    <Badge 
      className={`bg-gradient-to-r from-blue-500 to-cyan-500 text-white ${sizeClasses[size]}`}
      data-testid="early-adopter-badge"
    >
      <Rocket className="w-3 h-3 mr-1" />
      Early Adopter {number ? `#${number}` : ''}
    </Badge>
  );
}
